import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCalendar,
  FaBed,
  FaEdit,
  FaComment,
  FaIdCard,
  FaCamera,
  FaQrcode,
  FaBuilding,
  FaBriefcase,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaUserTag,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const GuestInfo = () => {
  const { guestId } = useParams();
  const [guestData, setGuestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingAadhaar, setUploadingAadhaar] = useState(false);
  const [uploadingGuestId, setUploadingGuestId] = useState(null);

  // Reassign room state
  const [modalOpen, setModalOpen] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [newRoomNumber, setNewRoomNumber] = useState('');

  // Remarks state
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [remarksBookingId, setRemarksBookingId] = useState('');
  const [remarkMessage, setRemarkMessage] = useState('');

  // Filter toggle state
  const [showOnlyCheckedIn, setShowOnlyCheckedIn] = useState(true);

  // Function to format booking status
  const formatBookingStatus = (status) => {
    if (status === 'Hard') return 'Checked In';
    if (status === 'Soft') return 'Reserved';
    return status || 'N/A';
  };

  // Function to get status color
  const getStatusColor = (status) => {
    if (status === 'Hard') return 'bg-green-100 text-green-800';
    if (status === 'Soft') return 'bg-yellow-100 text-yellow-800';
    if (status === 'Checkedout') return 'bg-blue-100 text-blue-800';
    if (status === 'Cancelled') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Fetch guest data
  const fetchGuestData = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/profile/bookings/?guestid=${guestId}`, {
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setGuestData(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuestData();
  }, [guestId]);

  // Upload Aadhaar card
  const handleAadhaarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingAadhaar(true);
    setUploadingGuestId(guestId);

    const formData = new FormData();
    formData.append('guest_id', guestId);
    formData.append('aadhar_file', file);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/update_profile`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload Aadhaar card');
      }

      const data = await response.json();

      if (data.aadhar_updated) {
        // Refetch guest data
        await fetchGuestData();
        toast.success('Aadhaar card uploaded and verified successfully!');
      } else {
        toast.warning('Aadhaar card uploaded but verification pending');
      }
    } catch (error) {
      console.error('Error uploading Aadhaar card:', error);
      toast.error(error.message || 'Failed to upload Aadhaar card');
    } finally {
      setUploadingAadhaar(false);
      setUploadingGuestId(null);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleReassignClick = (booking) => {
    setBookingId(booking.bookingid);
    setModalOpen(true);
  };

  const handleReassignRoom = async () => {
    if (!newRoomNumber) {
      toast.error('Please enter a room number');
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/reassign-room/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingid: bookingId,
          new_room_number: parseInt(newRoomNumber),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.message || `HTTP error! status: ${response.status}`);
      }

      toast.success(data.message);
      setModalOpen(false);
      setNewRoomNumber('');

      // Refresh guest data
      await fetchGuestData();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const handleRemarkClick = (booking) => {
    setRemarksBookingId(booking.bookingid);
    setRemarksModalOpen(true);
  };

  const handleSubmitRemark = async () => {
    if (!remarkMessage.trim()) {
      toast.error('Please enter a remark message');
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/bookings/${remarksBookingId}/remarks`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          remark: remarkMessage,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      toast.success(`Remark added successfully for booking ${data.booking_id}`);
      setRemarksModalOpen(false);
      setRemarkMessage('');
      await fetchGuestData();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F1A3A]"></div>
      </div>
    );

  if (error)
    return (
      <div className="text-center mt-8 text-red-500">Error: {error}. Please try again later.</div>
    );

  const guestInfo = guestData?.guest_info || {};
  const isCorporate = guestInfo.clienttype?.toLowerCase() === 'corporate';

  return (
    <div className="p-8 bg-[#F4F6FA] min-h-screen">
      <ToastContainer />

      {/* Back Navigation */}
      <Link
        to="/dashboard/core-modules/property-management-system/front-desk-operations"
        className="flex items-center text-[#4A5F7A] hover:text-[#0F1A3A] mb-8 transition-colors"
      >
        <FaArrowLeft className="mr-3" />
        <span className="text-lg font-medium">Back to Front Desk Operations</span>
      </Link>

      {/* Guest Profile Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Guest Information Card */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-[#E8ECF2] p-8 hover:shadow-md transition-shadow">
          <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {guestData?.face_embeddings &&
              guestData.face_embeddings.length > 0 &&
              guestData.face_embeddings[0]?.profilephoto ? (
                <img
                  src={guestData.face_embeddings[0].profilephoto}
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover shadow-md border-4 border-[#E8ECF2]"
                />
              ) : (
                <div className="w-36 h-36 rounded-full bg-[#F0F2F5] flex items-center justify-center shadow-md border-4 border-[#E8ECF2]">
                  <FaUser className="text-5xl text-[#6B7FA0]" />
                </div>
              )}
              {/* Guest Type Badge */}
              <div className="mt-3 text-center">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    isCorporate
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-purple-100 text-purple-800 border border-purple-200'
                  }`}
                >
                  {isCorporate ? <FaBriefcase className="mr-1" /> : <FaUserTag className="mr-1" />}
                  {guestInfo.clienttype || 'Leisure'}
                </span>
              </div>
            </div>

            {/* Guest Details */}
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-[#0F1A3A] mb-1">
                  {guestInfo.firstname} {guestInfo.lastname}
                </h1>
                <div className="flex items-center space-x-2 text-[#4A5F7A]">
                  <FaEnvelope className="text-sm" />
                  <span className="text-lg">{guestInfo.email}</span>
                </div>
              </div>

              {/* Corporate Details - Only show if corporate */}
              {isCorporate && (
                <div className="bg-blue-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <FaBuilding className="text-gray-600" />
                    <span className="font-semibold text-gray-800">Corporate Details</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#6B7FA0]">Company ID:</span>
                      <span className="ml-2 font-medium text-[#0F1A3A]">
                        {guestInfo.companyid || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#6B7FA0]">Company Name:</span>
                      <span className="ml-2 font-medium text-[#0F1A3A]">
                        {guestInfo.companyname || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 p-3 bg-[#F8F9FB] rounded-xl">
                  <FaPhone className="text-[#6B7FA0]" />
                  <div>
                    <p className="text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                      Phone
                    </p>
                    <p className="text-[#0F1A3A] font-medium">
                      {guestInfo.phone || 'Not Assigned'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-[#F8F9FB] rounded-xl">
                  <FaUser className="text-[#6B7FA0]" />
                  <div>
                    <p className="text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                      Client Type
                    </p>
                    <p className="text-[#0F1A3A] font-medium">
                      {guestInfo.clienttype || 'Not Assigned'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-[#F8F9FB] rounded-xl">
                  <FaCalendar className="text-[#6B7FA0]" />
                  <div>
                    <p className="text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                      Country
                    </p>
                    <p className="text-[#0F1A3A] font-medium">
                      {guestInfo.country || 'Not Assigned'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-[#F8F9FB] rounded-xl">
                  <FaCalendar className="text-[#6B7FA0]" />
                  <div>
                    <p className="text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                      Member Since
                    </p>
                    <p className="text-[#0F1A3A] font-medium">
                      {guestInfo.created_date
                        ? new Date(guestInfo.created_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : 'Not Assigned'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Aadhaar Verification Status */}
              <div className="flex items-center space-x-2 mt-2">
                {guestInfo.aadhaar_verified ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                    <FaCheckCircle className="mr-1" /> Aadhaar Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
                    <FaTimesCircle className="mr-1" /> Aadhaar Pending
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Aadhar Card Section with Upload */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#E8ECF2] p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center space-x-2 mb-4">
            <FaIdCard className="text-[#0F1A3A]" />
            <h3 className="text-xl font-bold text-[#0F1A3A]">Aadhaar Card</h3>
          </div>

          {guestInfo.aadhar_image_url ? (
            <div className="mb-4 relative group">
              <img
                src={guestInfo.aadhar_image_url}
                alt="Aadhaar Card"
                className="w-full h-64 object-cover border border-[#E8ECF2] rounded-xl shadow-sm"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-xl flex items-center justify-center">
                <a
                  href={guestInfo.aadhar_image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-0 group-hover:opacity-100 bg-white text-[#0F1A3A] px-4 py-2 rounded-lg font-medium shadow-lg transition-all duration-200"
                >
                  View Full Image
                </a>
              </div>
            </div>
          ) : (
            <div className="mb-4 w-full h-48 border-2 border-dashed border-[#E8ECF2] rounded-xl flex flex-col items-center justify-center bg-[#F8F9FB]">
              <FaIdCard className="text-4xl text-[#6B7FA0] mb-2" />
              <span className="text-[#4A5F7A] font-medium mb-1">No Aadhaar Card Uploaded</span>
              <span className="text-[#6B7FA0] text-sm text-center">
                Upload or capture the Aadhaar card
              </span>
            </div>
          )}

          {/* Aadhaar Upload Buttons */}
          <div className="flex flex-col space-y-3">
            <label className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 bg-[#0F1A3A] text-white font-semibold rounded-xl hover:bg-[#1B2F5C] transition-all duration-200 cursor-pointer">
              {uploadingAadhaar && uploadingGuestId === guestId ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaUpload />
              )}
              <span>
                {uploadingAadhaar && uploadingGuestId === guestId
                  ? 'Uploading...'
                  : guestInfo.aadhar_image_url
                    ? 'Replace Aadhaar'
                    : 'Upload Aadhaar'}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleAadhaarUpload}
                className="hidden"
                disabled={uploadingAadhaar}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Bookings Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#E8ECF2] p-8 hover:shadow-md transition-shadow">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl font-bold text-[#0F1A3A] mb-1">Reservation History</h2>
            <p className="text-[#4A5F7A]">Previous and current reservations for this guest</p>
          </div>

          <button
            onClick={() => setShowOnlyCheckedIn(!showOnlyCheckedIn)}
            className={`px-4 py-2 rounded-xl font-semibold transition-all duration-200 ${
              showOnlyCheckedIn
                ? 'bg-[#0F1A3A] text-white hover:bg-[#1B2F5C]'
                : 'border border-[#E8ECF2] text-[#0F1A3A] hover:bg-[#F8F9FB]'
            }`}
          >
            {showOnlyCheckedIn ? 'Show all reservations' : 'Show checked-in only'}
          </button>
        </div>

        <div className="overflow-x-auto shadow-sm rounded-xl border border-[#E8ECF2]">
          <table className="min-w-full bg-white">
            <thead className="bg-[#F8F9FB]">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Reservation Number
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Room
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Check-In
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Check-Out
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Guests
                </th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-[#6B7FA0] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8ECF2]">
              {guestData.bookings
                .filter((booking) => {
                  if (!showOnlyCheckedIn) return true;
                  return booking.booking_status === 'Hard';
                })
                .map((booking) => (
                  <tr key={booking.bookingid} className="hover:bg-[#F8F9FB] transition-colors">
                    <td className="px-4 py-4 text-sm font-medium text-[#0F1A3A]">
                      {booking.orderid || 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-[#0F1A3A]">
                      {booking.bookingid || 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(booking.booking_status)}`}
                      >
                        {formatBookingStatus(booking.booking_status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-[#4A5F7A]">
                      <div className="flex items-center space-x-2">
                        <FaBed className="text-[#6B7FA0]" />
                        <span className="font-medium">
                          {booking.room?.roomnumber || booking.roomnumber || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-[#4A5F7A]">
                      {booking.checkindate
                        ? new Date(booking.checkindate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-sm text-[#4A5F7A]">
                      {booking.checkoutdate
                        ? new Date(booking.checkoutdate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-sm text-[#4A5F7A]">
                      {booking.lengthofstay || 'N/A'} days
                    </td>
                    <td className="px-4 py-4 text-sm text-[#4A5F7A]">
                      {booking.numberofguests || 'N/A'}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="flex space-x-2">
                        <button
                          className="flex items-center space-x-1 px-3 py-1.5 border border-[#E8ECF2] text-[#0F1A3A] text-xs font-semibold rounded-lg hover:bg-[#F8F9FB] transition-all duration-200"
                          onClick={() => handleRemarkClick(booking)}
                        >
                          <FaComment />
                          <span>Remark</span>
                        </button>
                        {booking.booking_status === 'Hard' && (
                          <button
                            className="flex items-center space-x-1 px-3 py-1.5 bg-[#0F1A3A] text-white text-xs font-semibold rounded-lg hover:bg-[#1B2F5C] transition-all duration-200"
                            onClick={() => handleReassignClick(booking)}
                          >
                            <FaEdit />
                            <span>Re-Assign</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reassign Room Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-6">
              <FaBed className="text-xl text-[#0F1A3A]" />
              <h2 className="text-xl font-bold text-[#0F1A3A]">Re-Assign Room</h2>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-[#4A5F7A] mb-2">
                  Booking ID
                </label>
                <input
                  type="text"
                  value={bookingId}
                  readOnly
                  className="w-full p-3 border border-[#E8ECF2] rounded-xl bg-[#F8F9FB] text-[#0F1A3A] font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#4A5F7A] mb-2">
                  New Room Number *
                </label>
                <input
                  type="number"
                  value={newRoomNumber}
                  onChange={(e) => setNewRoomNumber(e.target.value)}
                  className="w-full p-3 border border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-transparent"
                  placeholder="Enter room number"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2.5 border border-[#E8ECF2] text-[#0F1A3A] font-semibold rounded-xl hover:bg-[#F8F9FB] transition-all duration-200"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2.5 bg-[#0F1A3A] text-white font-semibold rounded-xl hover:bg-[#1B2F5C] transition-all duration-200"
                onClick={handleReassignRoom}
              >
                Re-assign Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Remark Modal */}
      {remarksModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-6">
              <FaComment className="text-xl text-[#0F1A3A]" />
              <h2 className="text-xl font-bold text-[#0F1A3A]">Add Remark</h2>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-[#4A5F7A] mb-2">
                  Booking ID
                </label>
                <input
                  type="text"
                  value={remarksBookingId}
                  readOnly
                  className="w-full p-3 border border-[#E8ECF2] rounded-xl bg-[#F8F9FB] text-[#0F1A3A] font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#4A5F7A] mb-2">
                  Remark Message *
                </label>
                <textarea
                  value={remarkMessage}
                  onChange={(e) => setRemarkMessage(e.target.value)}
                  className="w-full p-3 border border-[#E8ECF2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-transparent resize-none"
                  rows="4"
                  placeholder="Enter your remark here..."
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2.5 border border-[#E8ECF2] text-[#0F1A3A] font-semibold rounded-xl hover:bg-[#F8F9FB] transition-all duration-200"
                onClick={() => {
                  setRemarksModalOpen(false);
                  setRemarkMessage('');
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2.5 bg-[#0F1A3A] text-white font-semibold rounded-xl hover:bg-[#1B2F5C] transition-all duration-200"
                onClick={handleSubmitRemark}
              >
                Submit Remark
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestInfo;
